import React from 'react';
import classNames from 'classnames';

import Data from 'data';
import Locale from 'data/locale';
import Store from 'store';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretRight, faCheck, faCheckCircle, faCopy, faTrashAlt, faUserSlash } from '@fortawesome/free-solid-svg-icons';

import { itemGetName } from 'logic/item';
import { D2ItemTooltip } from 'tooltips';
import { itemTypeTree, presetItems } from 'logic/itemTypes';
import { stateFindItem } from 'logic/stateReducers';
import Dialog from 'ui/dialog';
import { OverlayContext } from 'ui/utils/OverlayManager';
import EditingContext from 'store/editingContext';
import { inventoryFindItem } from 'logic/inventory';
import { itemNameColored } from './EquipmentPane';
import { EmbedItemLink, useContentCreator, useProfileId } from 'components/cclink';

const _L = Locale("ui-equipment");

export function NavigationMenu({ items, fullItems, types, onNavigate, path, vertical, activePath }) {
  return (
    <ul className={classNames("d2p-NavigationMenu", {"d2p-vertical": vertical})}>
      {items.map(item => {
        const index = fullItems.indexOf(item);
        const nextPath = (activePath && index === activePath[0] ? activePath.slice(1) : null);
        return <NavigationItem key={index} item={item} types={types} path={[...path, index]} onNavigate={onNavigate} activePath={nextPath}/>;
      })}
    </ul>
  );
}

function NavigationItem({ item, types, path=[], onNavigate, activePath, vertical, disabled }) {
  const [expanded, setExpanded] = React.useState(false);
  const onMouseEnter = React.useCallback(() => setExpanded(true), []);
  const onMouseLeave = React.useCallback(() => setExpanded(false), []);
  const onSubNavigate = React.useCallback(arg => {
    setExpanded(false);
    onNavigate(arg);
  }, [onNavigate]);
  const onClick = React.useCallback(() => onSubNavigate(path), [onSubNavigate, path]);

  const children = React.useMemo(() => (
    item.children?.filter(item => !item.hidden && (item.separator || (!types || types.some(type => item.filter(type)))))
      .filter((item, index, arr) => !item.separator || (index < arr.length - 1 && !arr[index + 1].separator))
  ), [item, types]);

  if (item.separator) {
    return <li className="d2p-NavigationItem d2p-separator">{_L(item.name)}</li>;
  }

  const expandable = children?.some(sub => !sub.separator && (!vertical || sub !== item.children[activePath?.[0]]));

  return (
    <li className={classNames("d2p-NavigationItem", {"d2p-expanded": expanded && (!vertical || expandable)})} onMouseEnter={disabled ? null : onMouseEnter} onMouseLeave={onMouseLeave}>
      <span className={classNames("d2p-label", {"d2p-special": item.hidden})} onClick={onClick}>
        {(!vertical && !!activePath) && <FontAwesomeIcon icon={faCheck} className="d2p-active"/>}
        {_L(item.name)}
        {(!vertical && expandable) && <FontAwesomeIcon icon={faCaretRight} className="d2p-expandable"/>}
      </span>
      {!!(expanded && expandable) && <NavigationMenu items={children} fullItems={item.children} types={types} path={path} onNavigate={onSubNavigate} activePath={activePath} vertical={vertical}/>}
    </li>
  );
}

function DeleteConfirmation({ locations, onResolve, onClose }) {
  return (
    <Dialog title={_L`Delete item`} modal onClose={onClose}>
      <Dialog.Content>
        <p>{_L`The item is used in the following locations:`}</p>
        <ul>
          {locations.map((loc, index) => (
            <li key={index}>
              {!!loc.item && _L`Socketed in ${(
                <D2ItemTooltip id={loc.id}>
                  <span className="d2p-ItemSocketedLink" onClick={() => onResolve(loc.id)}>{itemNameColored(loc.item)}</span>
                </D2ItemTooltip>
              )}`}
              {!!loc.profile && (
                <span>
                  {_L`Equipped in ${loc.profile.name}`}
                  {!!loc.mercenary && ` (${_L`Mercenary`})`}
                  {!!loc.inventory && ` (${_L`Inventory`})`}
                  {!!loc.cube && ` (${_L`Cube`})`}
                </span>
              )}
            </li>
          ))}
        </ul>
      </Dialog.Content>
      <Dialog.Footer>
        <button onClick={() => onResolve(true)}>{_L`Delete`}</button>
        <button onClick={() => onResolve(false)}>{_L`Cancel`}</button>
      </Dialog.Footer>
    </Dialog>
  );
}

export function navigateToItem(node, item) {
  const baseType = Data.items[item.base]?.type;
  const path = [];
  while (node.children) {
    const index = node.children.findIndex(item => item.filter?.(baseType) && !item.hidden);
    if (index < 0) break;
    path.push(index);
    node = node.children[index];
  }
  return path;
}

function NavigationItemLabel({ id, path, setPath, equipped }) {
  const item = Store.useItem(id) || presetItems[id];
  const inventoryIndex = EditingContext.useStateView("inventorySlotEditing");
  const cubeIndex = EditingContext.useStateView("cubeSlotEditing");
  const [cloned, setCloned] = React.useState(false);

  const isCC = useContentCreator();
  const profileId = useProfileId();

  React.useEffect(() => {
    if (cloned) {
      const id = setTimeout(() => setCloned(false), 2000);
      return () => clearTimeout(id);
    }
  }, [cloned]);

  const store = Store.useStore();
  const dispatch = Store.useDispatch();
  const overlays = React.useContext(OverlayContext);
  const editDispatch = EditingContext.useDispatch();
  const onDelete = React.useCallback(async () => {
    if (presetItems[id]) return;
    const state = store.getState();
    const locations = stateFindItem(state, id);
    if (locations.length) {
      const result = await overlays.async(<DeleteConfirmation locations={locations}/>);
      if (result !== true) {
        if (result && state.items[result]) {
          editDispatch({type: "item", id: result});
        }
        return;
      }
    }
    dispatch.setItem(id, null);

    let types = new Set();
    for (const oid in state.items) {
      if (oid === id) continue;
      const base = Data.items[state.items[oid]?.base];
      if (base) types.add(base.type);
    }
    types = [...types];

    const newPath = [];
    let current = itemTypeTree;
    for (const index of path) {
      const next = current.children?.[index];
      if (!next || next.separator) break;
      if (!next.hidden) {
        if (!types.some(type => next.filter(type))) {
          break;
        }
      }
      newPath.push(index);
      current = next;
    }
    setPath(newPath);
  }, [dispatch, id, overlays, store, path, setPath, editDispatch]);
  const onUnequip = React.useCallback(() => {
    const root = itemTypeTree.children[path[0]];
    if (root?.slot) {
      if (root.slot === "inventory" || root.slot === "cube") {
        dispatch.profile.setStorage(root.slot, (storage, _, state) => {
          const index = inventoryFindItem(storage, root.slot === "inventory" ? inventoryIndex : cubeIndex, state, Data.info[root.slot]);
          if (index < 0) return storage;
          storage = storage.slice();
          delete storage[index];
          return storage;
        });
      } else if (!root.merc) {
        dispatch.profile.setItem(root.slot, null);
      } else {
        dispatch.profile.setMercItem(root.slot, null);
      }
      setPath(path.slice(0, 1));
    }
  }, [path, dispatch, setPath, inventoryIndex, cubeIndex]);
  const onClone = React.useCallback(() => {
    const items = store.getState().items;
    let id = 1;
    while (items[id]) id += 1;
    id = id.toString();
    dispatch.setItem(id, item);

    const root = itemTypeTree.children[path[0]];
    if (root?.slot) {
      setPath([...path.slice(0, 1), ...navigateToItem(root, item), `item/${id}`]);
    } else {
      editDispatch({type: "item", id});
    }
    setCloned(true);
  }, [store, dispatch, item, path, setPath, editDispatch]);

  if (!item) return <li className="d2p-item-label d2p-disabled">{_L`Invalid Item`}</li>;

  return (
    <li>
      <D2ItemTooltip item={item}>
        <span className="d2p-item-label">{itemNameColored(item)}</span>
      </D2ItemTooltip>
      {!!(isCC && profileId) && <EmbedItemLink profile={profileId} id={id} text={itemGetName(item)}/>}
      {!presetItems[id] && equipped !== id && (
        <span className="d2p-item-action d2p-item-delete" onClick={onDelete}>
          <FontAwesomeIcon icon={faTrashAlt}/> {_L`Delete`}
        </span>
      )}
      {equipped === id && (
        <span className="d2p-item-action d2p-item-unequip" onClick={onUnequip}>
          <FontAwesomeIcon icon={faUserSlash}/> {_L`Unequip`}
        </span>
      )}
      {!cloned && !presetItems[id] && <span className="d2p-item-action d2p-item-clone" onClick={onClone}>
        <FontAwesomeIcon icon={faCopy}/> {_L`Clone`}
      </span>}
      {!!cloned && <span className="d2p-item-action d2p-item-cloned">
        <FontAwesomeIcon icon={faCheckCircle}/> {_L`Cloned!`}
      </span>}
    </li>
  );
}

export function NavigationBar({ path, setPath, types, disabled, equipped }) {
  let current = itemTypeTree;
  const list = [current];
  for (const index of path) {
    const next = current.children?.[index];
    if (next) {
      list.push(next);
    } else {
      const m = index.match(/^item\/(.*)$/);
      if (m) {
        list.push({elem: <NavigationItemLabel key="label" id={m[1]} path={path} setPath={setPath} equipped={equipped}/>});
      } else if (index === "new") {
        list.push({elem: <li key="new" className="d2p-item-label">{_L`Create Item`}</li>});
      }
    }
    current = next;
    if (!current) break;
  }

  return (
    <ul className="d2p-NavigationBar">
      {list.map((item, index) => {
        if (item.elem) return item.elem;
        const onNavigate = rel => setPath(path => {
          if (index === path.length && (!rel || !rel.length)) return path;
          path = path.slice(0, index);
          if (rel) path.push(...rel);
          return path;
        });
        return (
          <React.Fragment key={index}>
            <NavigationItem item={item} types={types} onNavigate={onNavigate} activePath={path.slice(index)} disabled={disabled} vertical/>
            {index < list.length - 1 && <li className="d2p-arrow"><FontAwesomeIcon icon={faCaretRight}/></li>}
          </React.Fragment>
        );
      })}
      {!!current && (
        <li className="d2p-grayed">
          <span className="d2p-arrow"><FontAwesomeIcon icon={faCaretRight}/></span>
          <span className="d2p-item-label" onClick={() => setPath(path => [...path, "new"])}>{_L`Create Item`}</span>
        </li>
      )}
    </ul>
  );
}
